<template>
  <pop-up-window
    :show="showResendDocument"
    :showClose="false"
    :width60="true"
    :bottomDiv="true"
    :positionAbsolute="true"
    :top10="true"
    fixed
  >
    <template #title>
      <h1 class="text-2xl">{{ $t("documentDetail.resendDocument.title") }}</h1>
    </template>
    <template #content>
      <div class="user-info divider" v-if="loaded">
        <div class="button-container-outer">
          <div class="button-container-inner">
            <div v-for="(isActive, type) in resendTypes" :key="type">
              <div
                @mouseenter="displayTooltip(type, true)"
                @mouseleave="displayTooltip(type, false)"
              >
                <button
                  class="btn custom-button text-popup"
                  :class="{ inactive: !isActive }"
                  v-if="displayButton(type)"
                  @click="makeActive(type)"
                  :disabled="isButtonDisabled[type]"
                >
                  {{ buttonTitle(type) }}
                </button>
              </div>
            </div>
          </div>
          <div class="tooltip-container">
            <div
              class="tooltip edi-tooltip"
              :class="{ 'display-tooltip': displayTooltipEdi }"
            >
              {{ ediToolTip }}
            </div>
          </div>
        </div>
        <div class="tooltip-container-attachMail">
          <div
            class="tooltip attachMail-tooltip"
            :class="{ 'display-tooltip': displayTooltipAttachMail }"
          >
            {{ ediToolTip }}
          </div>
        </div>
        <div>
          <resend-document-mail
            v-if="displayMail"
            :docId="docId"
            :legalEntityId="legalEntityId"
            :docsData="docsData"
            :mailType="mailType"
            :email="email"
            :client-id="clientId"
            :le-id="leIds"
            @resend="resendMail"
            @cancel="cancel"
          >
          </resend-document-mail>

          <resend-document-print
            v-else-if="displayPrint && !this.$store.state.resendBatch"
            :docId="docId"
            :legalEntityId="legalEntityId"
            :docsData="docsData"
            @close-pop-up="closePopUp"
            @cancel="cancel"
          >
          </resend-document-print>

          <resend-document-edi
            v-else-if="displayEdi"
            :docId="docId"
            :resend-from-main="resendFromMain"
            :docsData="docsData"
            :client-id="clientId"
            :le-id="leIds"
            @close-pop-up="closePopUp"
            @cancel="cancel"
            ref="resendEdi"
          >
          </resend-document-edi>
          <p v-else>
            {{ $t("documentDetail.resendDocument.noChannel") }}
          </p>

          <!--Only visible when no channel is selected-->
          <div class="mt-4" v-if="showCancelButton">
            <hr />
            <div class="btn-content">
              <button
                class="btn custom-button shadow-md mr-2 mt-4 text-popup"
                @click="cancel"
              >
                {{ $t("buttons.cancel") }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p>{{ $t("placeholders.loading") }}</p>
      </div>
    </template>
  </pop-up-window>
</template>

<script>
import PopUpWindow from "../../UI/PopUpWindow.vue";
import ResendDocumentEdi from "../resend/ResendDocumentEdi.vue";
import ResendDocumentPrint from "../resend/ResendDocumentPrint.vue";
import ResendDocumentMail from "../resend/ResendDocumentMail.vue";
import i18n from "../../../i18n";
import ConfirmationWithProps from "../../UI/ConfirmationWithProps.vue";
import userGroups from "../../../userGoups.js";

export default {
  beforeMount() {
    if (
      this.docId !== undefined &&
      this.$route.name !== "public" &&
      !userGroups.receiverNames.includes(
        this.$store.state.currentUserData.data.group,
      )
    )
      this.$store.dispatch("documents/readFileSize", {
        documentId: this.docId,
      });
  },
  name: "ResendDocument",
  components: {
    ConfirmationWithProps,
    ResendDocumentMail,
    ResendDocumentPrint,
    ResendDocumentEdi,
    PopUpWindow,
  },
  emits: ["unselectDocuments", "closePopUp", "closePopUpCancel"],
  props: {
    resendFromMain: {
      type: Boolean,
      required: false,
      default: false,
    },
    docId: {
      type: Array,
    },
    legalEntityId: {
      type: String,
    },
    docsData: {
      type: Array,
    },
    showResendDocument: Boolean,
    email: {
      type: String,
      required: false,
    },
  },
  async mounted() {
    this.ediResendCheck();
    this.setActiveTab();

    await this.displayDisable();
    this.loaded = true;
  },
  data() {
    return {
      currentDocId: this.docId,
      currentLegalEntityId: this.legalEntityId,
      activeType: "",
      disabled: true,
      ediCheck: {
        noVAT: false,
      },
      types: {
        attachMail: "attachMail",
        linkMail: "linkMail",
        print: "print",
        edi: "edi",
      },
      resendTypes: {
        attachMail: true,
        linkMail: false,
        print: false,
        edi: false,
      },
      i18n: i18n,
      displayTooltipEdi: false,
      displayTooltipAttachMail: false,
      showNoUbl: false,
      retryEdi: false,
      showDifferentIdentifiers: false,
      isButtonDisabled: {
        attachMail: false,
        linkMail: false,
        print: false,
        edi: false,
      },
      loaded: false,
      resendEmails: {},
    };
  },
  computed: {
    showCancelButton() {
      // Cancel button is only visible when no channel is selected
      return (
        !this.displayMail &&
        !(this.displayPrint && !this.$store.state.resendBatch) &&
        !this.displayEdi
      );
    },
    ediToolTip() {
      if (this.showNoUbl) {
        return i18n.t("buttons.ediDisabledTooltip.noValidUbl");
      } else if (this.showDifferentIdentifiers) {
        return i18n.t("buttons.ediDisabledTooltip.differentPeppolIdentifiers");
      } else if (this.$store.state.documentFileSizes.pdfSize >= 10) {
        return i18n.t("buttons.disableAtttachMailTooltip");
      } else if (this.retryEdi) {
        return i18n.t("buttons.ediDisabledTooltip.retry");
      }
      return "No Item to display";
    },

    mailType() {
      let mailType = "ATTACHMENT";
      if (this.activeType === this.types.linkMail) mailType = "LINK";
      return mailType;
    },
    displayEdi() {
      return (
        this.activeType === this.types.edi &&
        !this.isButtonDisabled[this.types.edi]
      );
    },
    displayMail() {
      const pdfSize = this.$store.state.documentFileSizes.pdfSize;
      if (
        this.activeType === this.types.print ||
        this.activeType === this.types.edi
      ) {
        return false;
      }

      if (
        this.activeType === this.types.attachMail ||
        this.activeType === this.types.linkMail
      ) {
        if (
          this.activeType === this.types.linkMail &&
          this.$store.state.supplierGroup.data.channelsConfig.linkEmail
        ) {
          this.makeActive(this.types.linkMail);
          return true;
        }
        if (pdfSize >= 10) {
          this.makeActive(this.types.linkMail);
          return true;
        }
        this.makeActive(this.types.attachMail);

        return true;
      }
      return false;
    },
    displayPrint() {
      return !!(
        this.activeType === this.types.print &&
        this.$store.state.supplierGroup.data.channelsConfig?.print?.printJobs &&
        this.$store.state.supplierGroup.data.channelsConfig.print.printProvider
      );
    },
    clientId() {
      let documentData = this.$store.getters.getDocumentDetailData;
      let clientId = "";
      if (documentData.billingData?.clientId) {
        clientId = documentData.billingData.clientId;
      } else if (documentData.opTableInfo) {
        clientId = documentData.opTableInfo.receiver.receiverId;
      } else if (documentData.generalDocumentData) {
        clientId = documentData.generalDocumentData.receiverId;
      } else if (documentData.salaryData)
        clientId = documentData.salaryData.employeeId;
      return clientId;
    },
    leIds() {
      const leIds = [];
      for (let le in this.$store.state.legalEntitiesData.data.legalEntities) {
        leIds.push(
          this.$store.state.legalEntitiesData.data.legalEntities[le]
            .entityNumber,
        );
      }
      return leIds;
    },
  },

  methods: {
    async customerMasterDataClient() {
      let response = await this.$store.dispatch("users/fetchReceiver", {
        formData: {
          receiverId: this.clientId,
          legalEntityIds: this.leIds,
          sort: [],
          sortOn: null,
          supplierGroupId: this.$store.state.supplierGroup.data.supplierGroupId,
        },
      });

      return response.data.data;
    },
    async resendMail(emails) {
      this.resendEmails = emails;
      let response = await this.customerMasterDataClient();

      let allMatch = {};
      if (response.items[0].channelInfo?.emails) {
        allMatch = Object.entries(response.items[0].channelInfo?.emails).every(
          ([_, mails]) =>
            mails.every((email) => emails.some((item) => item.email === email)),
        );
      }

      this.$emit("unselectDocuments");
      this.$emit("closePopUp");
    },
    closePopUp() {
      this.$emit("closePopUp");
    },
    cancel() {
      this.$emit("closePopUpCancel");
    },
    setActiveTab() {
      if (
        this.displayButton(this.types.attachMail) &&
        !this.isButtonDisabled[this.types.attachMail]
      )
        this.activeType = this.types.attachMail;
      else if (
        this.displayButton(this.types.linkMail) &&
        !this.isButtonDisabled[this.types.linkMail]
      )
        this.activeType = this.types.linkMail;
      else if (
        this.displayButton(this.types.print) &&
        !this.isButtonDisabled[this.types.print]
      )
        this.activeType = this.types.print;
      else if (
        this.displayButton(this.types.edi) &&
        !this.isButtonDisabled[this.types.edi]
      )
        this.activeType = this.types.edi;
      this.resendTypes = {
        attachMail: false,
        linkMail: false,
        print: false,
        edi: false,
      };
      this.resendTypes[this.activeType] = true;
    },
    ediResendCheck() {
      if (this.$store.state.resendBatch) {
        let noVats = [];
        this.docsData.forEach((doc) => {
          noVats.push(doc.noVat);
        });

        let uniqueNoVat = [...new Set(noVats)];
        this.ediCheck.noVAT = !(
          uniqueNoVat.length === 1 && uniqueNoVat[0] === false
        );
      } else if (this.$store.state.docType === "BILLING") {
        if (
          this.$store.state.documentDetailData.billingData?.billingTo
            ?.registration?.registrationNumber
        ) {
          this.ediCheck.noVAT = false;
        }
      }
    },
    displayTooltip(type, display) {
      if (type === this.types.edi && this.isButtonDisabled.edi)
        this.displayTooltipEdi = display;
      else if (
        type === this.types.attachMail &&
        this.isButtonDisabled.attachMail
      )
        this.displayTooltipAttachMail = display;
    },
    async displayDisable() {
      // set all vars on false
      Object.keys(this.isButtonDisabled).forEach((button) => {
        this.isButtonDisabled[button] = false;
      });
      this.showNoUbl = false;
      this.retryEdi = false;
      this.showDifferentIdentifiers = false;
      let ublAvailable = false;

      this.docsData.forEach((doc) => {
        if (doc.ublValid === undefined) ublAvailable = true;
      });

      let firstIdentifiers = "";
      let sameIdentifiers = true;

      if (this.docsData.length > 1 && !ublAvailable) {
        const res = await this.$store.dispatch("documents/getParticipantId", {
          docId: this.docsData[0].docId,
        });
        firstIdentifiers =
          res.data.data.participantId.schemeId +
          ":" +
          res.data.data.participantId.receiverId;

        for (const doc of this.docsData) {
          const res = await this.$store.dispatch("documents/getParticipantId", {
            docId: doc.docId,
          });
          const peppolIdentifier =
            res.data.data.participantId.schemeId +
            ":" +
            res.data.data.participantId.receiverId;
          if (firstIdentifiers !== peppolIdentifier) sameIdentifiers = false;
        }
        if (!sameIdentifiers) this.showDifferentIdentifiers = true;
      }
      if (ublAvailable) {
        this.showNoUbl = true;
        this.isButtonDisabled.edi = true;
        return;
      }
      this.docsData.forEach((doc) => {
        if (
          doc.documentData.channels.edi?.updates?.every(
            (u) => u.status === "SENT",
          )
        ) {
          this.isButtonDisabled.edi = true;
          this.retryEdi = true;
        }
      });
      if (this.docsData.length > 1 && !sameIdentifiers)
        this.isButtonDisabled.edi = true;
      if (this.$store.state.documentFileSizes.pdfSize >= 10) {
        this.isButtonDisabled.attachMail = true;
      }
    },
    displayButton(type) {
      let channelSetting = {
        attachEmail: true,
        linkEmail: true,
        print: true,
        edi: true,
      };

      this.docsData.forEach((docData) => {
        let currentLe = docData.documentData.legalEntity.entityNumber;
        const leSettings =
          this.$store.state.legalEntitiesData.data.legalEntities[currentLe]
            .channelsConfig;

        if (leSettings === undefined) {
          if (type === this.types.edi) {
            if (
              this.$store.state.docType !== "BILLING" ||
              this.$store.state.supplierGroup.data.channelsConfig?.edi ===
                undefined
            )
              channelSetting.edi = false;
            else if (this.$store.state.supplierGroup.data.channelsConfig.edi)
              channelSetting.edi = true;
          } else if (
            type === this.types.attachMail &&
            this.$store.state.supplierGroup.data.channelsConfig
          ) {
            channelSetting.attachEmail =
              this.$store.state.supplierGroup.data.channelsConfig.attachEmail;
          } else if (
            type === this.types.linkMail &&
            this.$store.state.supplierGroup.data.channelsConfig
          ) {
            channelSetting.linkEmail =
              this.$store.state.supplierGroup.data.channelsConfig.linkEmail;
          } else if (type === this.types.print) {
            channelSetting.print = !!(
              this.$store.state.supplierGroup.data.channelsConfig?.print
                ?.printJobs &&
              this.$store.state.supplierGroup.data.channelsConfig.print
                .printProvider &&
              !this.$store.state.resendBatch
            );
          }
        } else {
          if (!leSettings.attachEmail?.enabled)
            channelSetting.attachEmail = false;
          if (!leSettings.linkEmail?.enabled) channelSetting.linkEmail = false;
          if (!leSettings.print?.enabled || this.$store.state.resendBatch)
            channelSetting.print = false;
          if (!leSettings.edi?.enabled || this.$store.state.resendBatch)
            channelSetting.edi = false;
        }
      });
      if (type === this.types.attachMail) return channelSetting.attachEmail;
      else if (type === this.types.linkMail) return channelSetting.linkEmail;
      else if (type === this.types.print) return channelSetting.print;
      else if (type === this.types.edi) return channelSetting.edi;

      return false;
    },
    buttonTitle(type) {
      if (type === this.types.linkMail) return i18n.t("buttons.resendLink");
      if (type === this.types.print) return i18n.t("buttons.resendPrint");
      if (type === this.types.edi) {
        let ediProvider = false;
        if (
          this.$store.state.supplierGroup.data.channelsConfig?.edi
            ?.ediChannelProviders
        ) {
          Object.values(
            this.$store.state.supplierGroup.data.channelsConfig.edi
              .ediChannelProviders,
          ).forEach((provider) => {
            if (provider.provider !== "NYMUS_PEPPOL") ediProvider = true;
          });
        }

        if (ediProvider) return i18n.t("buttons.resendEdi");
        else return i18n.t("buttons.resendPeppol");
      }
      return i18n.t("buttons.resendAttach");
    },

    makeActive(resendType) {
      for (let type in this.resendTypes) {
        this.resendTypes[type] = false;
      }
      this.resendTypes[resendType] = true;
      this.activeType = resendType;
    },
  },
};
</script>

<style scoped>
.button-container-outer {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 100%;
  grid-auto-flow: row;
  justify-content: end;
}

.button-container-inner {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-self: end;
}

.button-container-inner button {
  margin-left: 10px;
}

.inactive {
  background-color: var(--theme-primary-100);
  border-color: rgba(85, 85, 85, var(--theme-primary-100));
}

.tooltip-container {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 24px;
  grid-auto-flow: row;
  justify-items: end;
}

.tooltip-container-attachMail {
  display: flex;
  justify-content: center;
}

.edi-tooltip {
  top: -5px;
  padding: 0 5px;
}

.attachMail-tooltip {
  top: -25px;
  justify-content: center;
}

.display-tooltip {
  display: block;
}

button:disabled:hover {
  display: block;
}
</style>
